import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { useTheme } from "../App";
import { api } from "../App";
import {
  Sparkles,
  Copy,
  Clock,
  TrendingUp,
  Sun,
  Moon,
  ArrowRight,
  CheckCircle2,
  Shield,
  Linkedin,
  Mail,
  Menu,
  X,
  Check,
  Zap,
  Image,
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/me");
        if (response.data) {
          if (response.data.has_completed_onboarding) {
            navigate("/feed", { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
        }
      } catch {
        // Not authenticated, stay on landing page
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const seoHead = (
    <Helmet>
      <title>PostWriter — The LinkedIn Ghostwriter for Engineers</title>
      <meta
        name="description"
        content="You ship great work — PostWriter makes sure LinkedIn knows. Posts built from your real engineering experience, grounded in research from GitHub, Hacker News, and Reddit."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://postwriter.tech/" />
      <meta property="og:title" content="PostWriter — The LinkedIn Ghostwriter for Engineers" />
      <meta
        property="og:description"
        content="You ship great work — PostWriter makes sure LinkedIn knows. Posts built from your real engineering experience, grounded in real research."
      />
      <meta property="og:image" content="https://postwriter.tech/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="PostWriter — You ship great work. Nobody on LinkedIn knows." />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://postwriter.tech/og-image.png" />
      <meta name="twitter:title" content="PostWriter — The LinkedIn Ghostwriter for Engineers" />
      <meta
        name="twitter:description"
        content="You ship great work — PostWriter makes sure LinkedIn knows. Posts built from your real engineering experience, grounded in real research."
      />
    </Helmet>
  );

  if (isCheckingAuth) {
    return (
      <>
        {seoHead}
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-background font-body">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-panel bg-background/80 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
              <span className="text-xl font-extrabold tracking-tighter font-headline text-foreground">
                PostWriter
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">Features</a>
              <a href="#how-it-works" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">How it Works</a>
              <a href="#pricing" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">Pricing</a>
              <a href="#testimonials" className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors">Early Access</a>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="hidden sm:inline-block font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors"
              >
                Log in
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>
              <button
                onClick={() => navigate("/signup")}
                data-testid="header-login-btn"
                className="bg-foreground text-background px-6 py-2.5 rounded-full font-headline font-bold text-sm uppercase tracking-wider shadow-lg transition-all active:scale-95"
                aria-label="Sign up to start your free trial"
              >
                Start Free
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-sm">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
              <a
                href="#features"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors py-2"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors py-2"
              >
                How it Works
              </a>
              <a
                href="#pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors py-2"
              >
                Pricing
              </a>
              <a
                href="#testimonials"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-headline font-bold tracking-tight text-xs uppercase text-muted-foreground hover:text-primary transition-colors py-2"
              >
                Early Access
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative px-6 md:px-12 py-20 lg:py-32 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 z-10">
              <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-secondary text-muted-foreground font-headline text-[10px] font-extrabold uppercase tracking-[0.2em]">
                Early Access
              </div>
              <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-[-0.03em] leading-[1.05] text-foreground mb-8">
                You ship great work.{" "}
                <span className="text-primary italic">Nobody on LinkedIn knows.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed mb-10">
                PostWriter turns your engineering experience into posts that sound like
                you — not like AI. Every draft is grounded in real research from GitHub,
                Hacker News, and the web, ready to edit and publish.
              </p>
              <div className="max-w-md">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    size="lg"
                    onClick={() => navigate("/signup")}
                    data-testid="hero-login-btn"
                    className="bg-foreground text-background px-8 py-6 rounded-full font-headline font-bold text-sm uppercase tracking-widest whitespace-nowrap shadow-xl hover:opacity-90 transition-all active:scale-95"
                  >
                    Try Free for 7 Days
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="mt-4 text-xs text-muted-foreground font-medium">
                  No credit card required. Free trial, no commitment.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative z-10 bg-card rounded-[2rem] shadow-2xl overflow-hidden ring-1 ring-border/20 transform lg:translate-x-6 rotate-2 hover:rotate-0 transition-transform duration-700">
                <div className="bg-muted p-4 flex items-center gap-2 border-b border-border/30">
                  <div className="w-3 h-3 rounded-full bg-red-300/40"></div>
                  <div className="w-3 h-3 rounded-full bg-primary/20"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400/30"></div>
                  <div className="ml-4 h-6 w-48 bg-muted-foreground/10 rounded-full"></div>
                </div>
                <div className="p-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-foreground flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-background" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-primary uppercase tracking-tighter">
                          Drafting Strategy
                        </div>
                        <div className="text-sm font-semibold text-foreground">
                          The Future of Edge Computing
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-xl text-sm leading-relaxed text-muted-foreground font-medium">
                      "Edge computing isn't just a trend; it's the decentralized
                      backbone of the next AI wave. Here's why every CTO needs to
                      shift their focus..."
                    </div>
                    <button className="w-full py-3 bg-primary/5 text-primary border border-primary/20 rounded-lg font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                      <Copy className="w-3.5 h-3.5" />
                      Copy to LinkedIn
                    </button>
                  </div>
                </div>
              </div>
              <div className="absolute -top-10 -right-10 w-64 h-64 bg-foreground opacity-5 rounded-full blur-3xl -z-0"></div>
            </div>
          </div>
        </section>

        {/* Value Strip */}
        <section className="bg-muted/50 py-12 mb-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
              {[
                { label: "Sources scanned per post", value: "10–20" },
                { label: "Time to first draft", value: "~90s" },
                { label: "Hours of writing saved", value: "2+ hrs" },
              ].map(({ label, value }) => (
                <div key={label} className="flex flex-col items-center gap-1">
                  <span className="text-2xl font-headline font-extrabold text-foreground">{value}</span>
                  <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-muted-foreground font-body text-xs font-bold tracking-widest uppercase mb-6">
              Process
            </span>
            <h2 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight max-w-3xl">
              Here's exactly{" "}
              <span className="text-primary italic">what happens.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            {[
              {
                step: "01",
                title: "Tell PostWriter who you are.",
                desc: "Your role, your stack, your topics — and real moments from your career. PostWriter writes from what you've actually built, not invented stories.",
              },
              {
                step: "02",
                title: "PostWriter scans the web for what's happening.",
                desc: "GitHub Trending, Hacker News, industry news, Reddit — it pulls fresh, real sources so every post is grounded in something worth saying.",
              },
              {
                step: "03",
                title: "Edit one draft. Post it.",
                desc: "You get a post that already did the research. Tweak it, make it yours, publish. Total time: under 5 minutes.",
              },
            ].map((item, idx) => (
              <div key={idx} className="group">
                <div className="mb-8 flex items-baseline gap-4">
                  <span className="text-6xl font-headline font-extrabold text-muted/80 group-hover:text-primary/30 transition-colors duration-500">
                    {item.step}
                  </span>
                  <div className="h-px flex-grow bg-border group-hover:bg-primary transition-colors duration-500"></div>
                </div>
                <h3 className="font-headline text-2xl font-bold mb-4">
                  {item.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-lg">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Bento Feature Grid */}
        <section id="features" className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
          <div className="mb-16">
            <h2 className="font-headline text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
              What PostWriter does for you.
            </h2>
            <div className="w-20 h-1 bg-foreground rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Full-width card — My Experiences (hero feature) */}
            <div className="md:col-span-3 bg-card rounded-[2rem] p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)] ring-1 ring-primary/20 flex flex-col md:flex-row gap-10 items-center">
              <div className="flex-1">
                <Sparkles className="w-9 h-9 text-primary mb-6" />
                <h3 className="font-headline text-2xl font-bold mb-4">
                  Your career is the content.
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Add real moments from your career — the migration you led, the outage
                  you fixed at 3am, the architecture call that paid off. PostWriter
                  writes posts grounded in what you've actually done, not invented
                  stories that sound like everyone else's AI feed.
                </p>
                <p className="text-sm font-semibold text-primary">
                  No other LinkedIn tool does this.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="bg-muted/50 rounded-xl p-5 space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] font-bold font-headline uppercase tracking-widest text-muted-foreground pt-1 shrink-0 w-24">You add</span>
                    <span className="text-muted-foreground leading-relaxed">"Migrated 200GB Postgres DB to Aurora with zero downtime"</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-[10px] font-bold font-headline uppercase tracking-widest text-primary pt-1 shrink-0 w-24">You get</span>
                    <span className="text-foreground leading-relaxed font-medium">"Zero-downtime migrations aren't about the tooling. When we moved 200GB to Aurora, the thing that saved us wasn't AWS DMS — it was the rollback plan we never used…"</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Wide card — Tech-Tailored Trends */}
            <div className="md:col-span-2 bg-muted/40 dark:bg-card rounded-[2rem] p-10 flex flex-col justify-between overflow-hidden relative group">
              <div>
                <TrendingUp className="w-9 h-9 text-primary mb-6" />
                <h3 className="font-headline text-2xl font-bold mb-4">
                  Tech-Tailored Trends
                </h3>
                <p className="text-muted-foreground max-w-sm leading-relaxed">
                  Built for engineers, not marketers. PostWriter scans GitHub trends,
                  HN top stories, and tech whitepapers to write content that holds up
                  in front of a technical audience.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-3 gap-3">
                {["AI/ML", "DevOps", "Frontend", "Cloud", "Security", "Data"].map(
                  (tag) => (
                    <span
                      key={tag}
                      className="px-3 py-2 bg-background rounded-xl text-xs font-semibold text-muted-foreground text-center"
                    >
                      {tag}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Stat card */}
            <div className="bg-foreground text-background rounded-[2rem] p-10 flex flex-col justify-center text-center">
              <div className="text-5xl font-extrabold font-headline mb-2">0</div>
              <p className="font-headline text-xs font-bold uppercase tracking-widest opacity-80 mb-6">
                Blank Pages
              </p>
              <p className="text-sm leading-relaxed opacity-90">
                PostWriter starts from real research — GitHub trending, HN top stories,
                and fresh web sources — so you never stare at an empty text box again.
              </p>
            </div>

            {/* Small card — 3 Minutes a Day */}
            <div className="bg-muted/40 dark:bg-card rounded-[2rem] p-10 flex flex-col justify-between">
              <div>
                <Clock className="w-9 h-9 text-foreground mb-6" />
                <h3 className="font-headline text-xl font-bold mb-4">
                  3 Minutes a Day
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Spend your morning coffee picking a prompt, tweaking a line, and
                  hitting post. Total authority, zero friction.
                </p>
              </div>
              <div className="mt-10 space-y-3">
                {[
                  { time: "7:00am", action: "Pick a topic from PostWriter's daily suggestions" },
                  { time: "7:02am", action: "Review the research trail and draft" },
                  { time: "7:05am", action: "Edit one line, hit publish" },
                ].map(({ time, action }) => (
                  <div key={time} className="flex items-start gap-3">
                    <span className="text-[10px] font-bold font-headline uppercase tracking-widest text-primary pt-0.5 shrink-0">{time}</span>
                    <span className="text-xs text-muted-foreground leading-relaxed">{action}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Wide card — Writing Frameworks */}
            <div className="md:col-span-2 bg-card rounded-[2rem] p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)] ring-1 ring-border/10 flex flex-col md:flex-row gap-10 items-center">
              <div className="flex-1">
                <h3 className="font-headline text-2xl font-bold mb-4">
                  Pick how you want to sound.
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  PostWriter generates drafts in different narrative styles — the
                  contrarian take, the data-backed argument, the personal story. Pick
                  the one that fits what you want to say.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="bg-muted/50 rounded-xl p-4 space-y-2 text-xs">
                  {[
                    { style: "Contrarian take", desc: "Challenge the consensus with evidence" },
                    { style: "Data-backed", desc: "Lead with numbers, follow with insight" },
                    { style: "Personal story", desc: "First person, specific moment, lesson" },
                    { style: "Hot take", desc: "Provocative opening, defend it in the thread" },
                  ].map(({ style, desc }) => (
                    <div key={style} className="flex items-center gap-3 py-1.5 border-b border-border/50 last:border-0">
                      <span className="font-bold text-foreground shrink-0 w-28">{style}</span>
                      <span className="text-muted-foreground">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-muted-foreground font-body text-xs font-bold tracking-widest uppercase mb-6">
              Pricing
            </span>
            <h2 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight">
              Simple,{" "}
              <span className="text-primary italic">transparent</span> pricing.
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-xl mx-auto">
              Start free for 7 days. No credit card required.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free */}
            <div className="bg-muted/40 dark:bg-card rounded-[2rem] p-8 flex flex-col ring-1 ring-border/20">
              <div className="mb-6">
                <span className="font-headline text-xs font-extrabold uppercase tracking-widest text-muted-foreground">
                  Free Trial
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-headline font-extrabold text-foreground">$0</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">7 days, no card needed</p>
              </div>
              <ul className="space-y-3 flex-1 mb-8">
                {[
                  "5 posts per day",
                  "All post frameworks",
                  "Research-backed content",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="rounded-xl border border-border bg-background px-4 py-2.5 text-center text-sm font-semibold text-muted-foreground">
                Current free plan
              </div>
            </div>

            {/* Pro — highlighted */}
            <div className="relative bg-card rounded-[2rem] p-8 flex flex-col ring-2 ring-foreground/20 shadow-xl">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="bg-foreground text-background text-[10px] font-headline font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-md">
                  Recommended
                </span>
              </div>
              <div className="mb-6">
                <span className="font-headline text-xs font-extrabold uppercase tracking-widest text-primary">
                  Pro
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-headline font-extrabold text-foreground">$19.99</span>
                  <span className="text-sm text-muted-foreground font-medium">/mo</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Cancel anytime</p>
              </div>
              <ul className="space-y-3 flex-1 mb-8">
                {[
                  "Unlimited posts per day",
                  "Carousel post creation",
                  "All post frameworks",
                  "Research from Reddit, GitHub & more",
                  "Priority support",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/signup")}
                className="bg-foreground text-background w-full py-3 rounded-xl font-headline font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:opacity-90 active:scale-95 transition-all"
              >
                <Zap className="w-4 h-4" />
                Get Started
              </button>
            </div>

            {/* Ultra */}
            <div className="relative bg-card rounded-[2rem] p-8 flex flex-col ring-2 ring-border shadow-xl">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="bg-secondary text-foreground text-[10px] font-headline font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-md">
                  Most Powerful
                </span>
              </div>
              <div className="mb-6">
                <span className="font-headline text-xs font-extrabold uppercase tracking-widest text-muted-foreground">
                  Ultra
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-headline font-extrabold text-foreground">$34.99</span>
                  <span className="text-sm text-muted-foreground font-medium">/mo</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Cancel anytime</p>
              </div>
              <ul className="space-y-3 flex-1 mb-8">
                {[
                  "Everything in Pro",
                  "AI image generation per post",
                  "Infographic creation",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-foreground" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/signup")}
                className="w-full py-3 rounded-xl bg-foreground text-background font-headline font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:opacity-90 active:scale-95 transition-all"
              >
                <Image className="w-4 h-4" />
                Get Started
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-8">
            Payments securely processed by Lemon Squeezy. All taxes handled automatically.
          </p>
        </section>

        {/* Early Access CTA */}
        <section id="testimonials" className="py-24 px-6 md:px-12 max-w-7xl mx-auto text-center">
          <span className="inline-block font-body text-primary font-bold tracking-[0.2em] uppercase text-xs mb-4">
            Early Access
          </span>
          <h2 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter mb-6 text-foreground">
            PostWriter is new. We're looking for honest early users.
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed">
            Built for engineers, EMs, and CTOs — not marketers. No inflated numbers, no fake
            reviews. If you ship real work and want LinkedIn to know, try it free for 7 days
            and tell us what's broken. That feedback shapes what we build next.
          </p>
          <button
            onClick={() => navigate("/signup")}
            data-testid="social-proof-login-btn"
            className="bg-foreground text-background px-10 py-4 rounded-full font-headline font-bold text-sm uppercase tracking-widest shadow-lg hover:opacity-90 transition-all active:scale-95"
          >
            Start Your Free Trial
          </button>
          <p className="mt-4 text-xs text-muted-foreground">7 days free. No credit card required.</p>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="relative overflow-hidden rounded-[2rem] bg-gray-900 dark:bg-gray-950 p-12 md:p-24 text-center">
              <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 bg-primary/20 rounded-full blur-[100px]"></div>
              <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 bg-primary/10 rounded-full blur-[100px]"></div>
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="font-headline text-4xl md:text-6xl font-extrabold tracking-tighter text-white mb-8 leading-tight">
                  Your next LinkedIn post is already out there.
                </h2>
                <p className="text-lg md:text-xl text-gray-300 mb-12 leading-relaxed">
                  PostWriter finds it, writes the draft, and hands it to you in under 2 minutes.
                  No blank page. No guessing. Just a post worth publishing.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <Button
                    size="lg"
                    onClick={() => navigate("/signup")}
                    data-testid="cta-login-btn"
                    className="bg-primary text-primary-foreground px-8 py-6 rounded-full font-headline font-bold text-sm uppercase tracking-widest shadow-xl hover:opacity-90 transition-all active:scale-95"
                  >
                    Start Free Trial
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="mt-12 flex items-center justify-center gap-8 text-gray-400 text-sm">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <span>7-day free trial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span>No credit card required</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-muted/30 w-full py-12 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 px-6 md:px-12 max-w-7xl mx-auto">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
                <span className="text-lg font-bold font-headline text-foreground">PostWriter</span>
              </div>
              <p className="text-muted-foreground text-sm mb-6">
                &copy; 2026 PostWriter. The LinkedIn ghostwriter for engineers.
              </p>
            </div>
            <div>
              <h4 className="font-headline font-bold text-foreground mb-4 uppercase tracking-wider text-xs">
                Product
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#features"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#how-it-works"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    How It Works
                  </a>
                </li>
                <li>
                  <a
                    href="#testimonials"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Testimonials
                  </a>
                </li>
                <li>
                  <a
                    href="/compare"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Compare vs Taplio &amp; others
                  </a>
                </li>
                <li>
                  <a
                    href="/post-ideas"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Post ideas by role
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-headline font-bold text-foreground mb-4 uppercase tracking-wider text-xs">
                Legal
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="/privacy"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:amrit@postwriter.tech"
                    className="text-muted-foreground hover:text-foreground underline decoration-primary/30 underline-offset-4 transition-all text-sm"
                  >
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-headline font-bold text-foreground mb-4 uppercase tracking-wider text-xs">
                Connect
              </h4>
              <div className="flex gap-4">
                <a
                  className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-all"
                  href="https://www.linkedin.com/in/amrits"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PostWriter on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-all"
                  href="mailto:amrit@postwriter.tech"
                  aria-label="Email PostWriter"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Featured on LaunchBison */}
          <div className="mt-10 pt-8 border-t border-border px-6 md:px-12 max-w-7xl mx-auto flex justify-center">
            <a
              href="https://launchbison.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="PostWriter featured on LaunchBison"
            >
              <img
                src="https://launchbison.com/badge-light.svg"
                alt="Featured on LaunchBison"
                width="198"
                height="62"
                loading="lazy"
                decoding="async"
                className="dark:hidden"
              />
              <img
                src="https://launchbison.com/badge-dark.svg"
                alt="Featured on LaunchBison"
                width="198"
                height="62"
                loading="lazy"
                decoding="async"
                className="hidden dark:block"
              />
            </a>
          </div>
        </footer>
      </main>
      </div>
    </>
  );
};

export default LandingPage;
